'use client';

import DesktopEstimateShipping from 'app/product/[slug]/components/EstimateShipping/DesktopEstimateShipping';
import MobileEstimateShipping from 'app/product/[slug]/components/EstimateShipping/MobileEstimateShipping';
import useRealTimeBids from '@util/hooks/auctions/useRealtimeBids';
import {
  cn,
  formatCurrency,
  getCountryLabelFromCode,
  isMobile,
} from '@util/index';
import { ChevronDownIcon, RefreshCwIcon } from 'lucide-react';
import {
  isVehicleProduct,
  type ProductDocument,
  type Variation,
} from 'models/product';
import { useState } from 'react';
import ProductCouponDisplay from '@c/ProductCouponDisplay';
import { useBikeFilter } from 'context/BikeFilterContext';
import { useWarehouseVariations } from '@util/hooks/useWarehouseVariations';

const ProductPriceAndShipping = ({
  product,
  selectedVariations,
  shipping,
  customInputsEnabled,
}: {
  product: ProductDocument;
  selectedVariations?: Variation[] | null;
  shipping?: {
    code: string;
    cost: number;
    name: string;
    estimated?: boolean;
  } | null;
  customInputsEnabled?: boolean;
}) => {
  const { highestBid } = useRealTimeBids(product.id);
  const { selectedBike } = useBikeFilter();
  const [showShippingOptions, setShowShippingOptions] = useState(false);
  const [showMobileEstimateShipping, setShowMobileEstimateShipping] =
    useState(false);
  const [showDesktopEstimateShipping, setShowDesktopEstimateShipping] =
    useState(false);
  const [estimatedShipping, setEstimatedShipping] = useState<{
    code: string;
    cost: number;
    name: string;
    estimated?: boolean;
  } | null>(null);

  // Fetch warehouse variations to get related items for price range calculation
  const { relatedItems } = useWarehouseVariations(product);
  const getOgPrice = () => {
    if (!product.has_variations || !selectedVariations?.length) {
      return product.og_price;
    }

    const letterSize = selectedVariations?.find((v) => Number.isNaN(+v.size));
    const numberSize = selectedVariations?.find(
      (v) => !Number.isNaN(+v.size) && v.is_number
    );
    const isColor = selectedVariations?.find((v) => !!v.color);

    if (letterSize && numberSize && !isColor) {
      const discountRatio = product.price / product.og_price;
      const selectedPrice = letterSize.price + numberSize.price;
      return selectedPrice / discountRatio;
    }

    if (letterSize) {
      const selectedVariation = isColor
        ? product.variations?.find(
            (v) => v.color === isColor.color && v.size === letterSize.size
          )
        : letterSize;

      if (selectedVariation) {
        const discountRatio = product.price / product.og_price;
        return selectedVariation.price / discountRatio;
      }
    }

    if (numberSize) {
      const selectedVariation = isColor
        ? product.variations?.find(
            (v) => v.color === isColor.color && v.size === numberSize.size
          )
        : numberSize;

      if (selectedVariation) {
        const discountRatio = product.price / product.og_price;
        return selectedVariation.price / discountRatio;
      }
    }

    return product.og_price;
  };

  const getPrice = () => {
    if (product.is_auction) {
      return highestBid?.bid_price || product.price;
    }
    let basePrice = Math.max(
      product.price,
      Math.max(...(product.variations?.map((v) => v.price) ?? [0]))
    );
    if (selectedVariations) {
      const letterSize = selectedVariations?.find((v) => Number.isNaN(+v.size));
      const numberSize = selectedVariations?.find(
        (v) => !Number.isNaN(+v.size) && v.is_number
      );
      const isColor = selectedVariations?.find((v) => !!v.color);
      if (letterSize && numberSize && !isColor) {
        basePrice = letterSize.price + numberSize.price;
      } else if (letterSize) {
        if (isColor) {
          basePrice =
            product.variations?.find(
              (v) => v.color === isColor.color && v.size === letterSize.size
            )?.price || letterSize.price;
        } else {
          basePrice = letterSize.price;
        }
      } else if (numberSize) {
        if (isColor) {
          basePrice =
            product.variations?.find(
              (v) => v.color === isColor.color && v.size === numberSize.size
            )?.price || numberSize.price;
        } else {
          basePrice = numberSize.price;
        }
      }
    }

    if (customInputsEnabled && product.custom_inputs) {
      basePrice += product.custom_inputs.cost;
    }

    return basePrice;
  };

  const ogPrice = getOgPrice();
  const salePercent = Math.round(
    ((Number(ogPrice) - getPrice()) / Number(ogPrice)) * 100
  );
  const displayedShipping = estimatedShipping ?? shipping;
  const hasValidDiscount =
    product.on_sale &&
    !product.is_auction &&
    salePercent > 0 &&
    (!product.has_variations || selectedVariations?.length);
  const isFreeShipping =
    (product.is_flat_rate &&
      product.shipping_costs?.every(({ cost }) => cost === 0)) ||
    displayedShipping?.cost === 0;

  // Check if this is a warehouse product with no vehicle selected
  const isWarehouseProduct = !!product.group_id;
  const isVehicleSelected =
    selectedBike?.year && selectedBike?.make && selectedBike?.model;
  const hasFittingBikes = (product.fitting_bikes?.bikes?.length ?? 0) > 0;
  const otherProductsInGroup = relatedItems.filter(
    (item) => item.id !== product.id
  );
  const shouldShowPriceRange =
    isWarehouseProduct &&
    !isVehicleSelected &&
    hasFittingBikes &&
    otherProductsInGroup.length > 0;

  // Calculate price range from related items (excluding current product)
  const getPriceRange = () => {
    if (!shouldShowPriceRange || relatedItems.length === 0) {
      return null;
    }

    const prices = relatedItems
      .map((item) => {
        // Calculate price similar to getPrice() logic but for each related item
        if (item.is_auction) {
          return item.price;
        }
        let basePrice = Math.max(
          item.price,
          Math.max(...(item.variations?.map((v) => v.price) ?? [0]))
        );
        return basePrice;
      })
      .filter((price) => price > 0);

    if (prices.length === 0) {
      return null;
    }

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    // Don't show range if min and max are the same
    if (minPrice === maxPrice) {
      return null;
    }

    return { minPrice, maxPrice };
  };

  const priceRange = getPriceRange();

  return (
    <div className={cn('flex flex-col gap-y-6')}>
      <div className="flex flex-col gap-y-2">
        {hasValidDiscount && (
          <div className="mb-4 w-fit rounded-[1rem] bg-brand-secondary px-6 py-2 text-[1.8rem] font-semibold text-white">
            {salePercent}% OFF
          </div>
        )}

        <div className="flex items-center gap-4 pt-2">
          {shouldShowPriceRange && priceRange ? (
            <p
              className={cn(
                'text-[3rem] font-semibold leading-[2.4rem]',
                product.on_sale ? 'text-brand-secondary' : 'text-brand-primary'
              )}
            >
              {formatCurrency(priceRange.minPrice)} -{' '}
              {formatCurrency(priceRange.maxPrice)}
            </p>
          ) : (
            <>
              <p
                className={cn(
                  'text-[3rem] font-semibold leading-[2.4rem]',
                  product.on_sale
                    ? 'text-brand-secondary'
                    : 'text-brand-primary'
                )}
              >
                {formatCurrency(getPrice())}
              </p>

              {hasValidDiscount && (
                <p className="text-[1.8rem] text-zinc-500">
                  Was{' '}
                  <span className="line-through">
                    {formatCurrency(ogPrice)}
                  </span>
                </p>
              )}
            </>
          )}
        </div>

        {!isVehicleProduct(product) && (
          <>
            {displayedShipping ? (
              <>
                {isFreeShipping && !product.is_digital ? (
                  <div className="relative mt-2 flex items-center gap-x-2">
                    <div className="flex items-center gap-2">
                      <div className="w-fit rounded-2xl bg-black px-4 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-[1.4rem] font-semibold uppercase text-white">
                            Free Shipping
                          </span>
                        </div>
                      </div>

                      <span className="text-[1.4rem] text-black">to</span>

                      <span className="text-[1.4rem] font-medium text-black">
                        {getCountryLabelFromCode(displayedShipping.code)}
                      </span>
                    </div>

                    {!!product.shipping_costs?.length && (
                      <>
                        {!displayedShipping.estimated ? (
                          <button
                            type="button"
                            className="border-none bg-inherit"
                            aria-label="Show shipping options"
                            onClick={() =>
                              setShowShippingOptions(!showShippingOptions)
                            }
                          >
                            <ChevronDownIcon
                              className={cn(
                                'h-8 w-8 text-zinc-700',
                                showShippingOptions ? 'rotate-180' : ''
                              )}
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="border-none bg-inherit"
                            aria-label="Redo estimate"
                            onClick={() => {
                              if (isMobile()) {
                                setShowMobileEstimateShipping(true);
                              } else {
                                setShowDesktopEstimateShipping(true);
                              }
                            }}
                          >
                            <RefreshCwIcon className="h-8 w-8 text-zinc-700" />
                          </button>
                        )}
                      </>
                    )}

                    {showShippingOptions && (
                      <div className="absolute right-0 top-full z-10 w-full cursor-default rounded-[1.5rem] border border-zinc-200 bg-zinc-50 px-6 py-2 shadow-md">
                        {product.shipping_costs?.map(({ code, cost }) => (
                          <div
                            className="flex items-center gap-x-2 py-2 text-[1.6rem]"
                            key={code}
                          >
                            + {formatCurrency(cost)} Shipping ({code})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : !product.is_digital ? (
                  <div className="relative mt-2 flex items-center gap-x-2">
                    <div className="flex items-center gap-4 font-medium">
                      <span className="text-[1.4rem] text-zinc-500">
                        + {formatCurrency(displayedShipping.cost)} Shipping to{' '}
                        {getCountryLabelFromCode(displayedShipping.code)}
                      </span>
                    </div>

                    {!!product.shipping_costs?.length && (
                      <>
                        {!displayedShipping.estimated ? (
                          <button
                            type="button"
                            className="border-none bg-inherit"
                            aria-label="Show shipping options"
                            onClick={() =>
                              setShowShippingOptions(!showShippingOptions)
                            }
                          >
                            <ChevronDownIcon
                              className={cn(
                                'h-8 w-8 text-zinc-700',
                                showShippingOptions ? 'rotate-180' : ''
                              )}
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="border-none bg-inherit"
                            aria-label="Redo estimate"
                            onClick={() => {
                              if (isMobile()) {
                                setShowMobileEstimateShipping(true);
                              } else {
                                setShowDesktopEstimateShipping(true);
                              }
                            }}
                          >
                            <RefreshCwIcon className="h-8 w-8 text-zinc-700" />
                          </button>
                        )}
                      </>
                    )}

                    {showShippingOptions && (
                      <div className="absolute right-0 top-full z-10 w-full cursor-default rounded-[1.5rem] border border-zinc-200 bg-zinc-50 px-6 py-2 shadow-md">
                        {product.shipping_costs?.map(({ code, cost }) => (
                          <div
                            className="flex items-center gap-x-2 py-2 text-[1.6rem]"
                            key={code}
                          >
                            + {formatCurrency(cost)} Shipping ({code})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </>
            ) : displayedShipping === null ? (
              <>
                <button
                  type="button"
                  className="mt-2 text-start text-[1.4rem] text-zinc-500 underline hover:text-zinc-700"
                  onClick={() => {
                    if (isMobile()) {
                      setShowMobileEstimateShipping(true);
                    } else {
                      setShowDesktopEstimateShipping(true);
                    }
                  }}
                >
                  + est. shipping
                </button>
              </>
            ) : (
              <div className="h-6 w-48 animate-pulse rounded-xl bg-brand-lightest-gray" />
            )}
          </>
        )}
      </div>

      {!isVehicleProduct(product) && (
        <ProductCouponDisplay product={product} />
      )}

      <DesktopEstimateShipping
        product={product}
        isOpen={showDesktopEstimateShipping}
        dismiss={(shippingCost) => {
          setEstimatedShipping(shippingCost);
          setShowDesktopEstimateShipping(false);
        }}
      />

      <MobileEstimateShipping
        product={product}
        show={showMobileEstimateShipping}
        dismiss={(shippingCost) => {
          setEstimatedShipping(shippingCost);
          setShowMobileEstimateShipping(false);
        }}
      />
    </div>
  );
};

export default ProductPriceAndShipping;
